/**
 * Driver GPS write policy (shared by native + mobile-web tracking).
 *
 * The native watcher (distance-filtered) is the ONLY routine source of
 * location readings. Nothing here polls the GPS.
 *  - GpsWritePolicy    decides whether a supplied reading is written (first /
 *                      >300m jump / ≥60s since last write). Stops per-second
 *                      web writes.
 *  - LowPowerRecovery  after ~2 min with no save while EN_ROUTE: ONE
 *                      low-accuracy request per silent period (stationary
 *                      confirmation or one watcher restart). No timer of its own.
 *  - StaleWatcherGuard existing 5-min silent-watcher restart backstop, with
 *                      back-off so restarts cannot loop.
 *  - GpsRetryQueue     bounded, age-limited, ordered retry of failed writes.
 */

export const GPS_MAX_INTERVAL_MS = 60_000;
export const GPS_MIN_DISTANCE_M = 75;
export const GPS_FORCE_DISTANCE_M = 300;
/** Existing watcher-health backstop: silent for 5 min → restart. */
export const STALE_WATCHER_MS = 5 * 60_000;
export const STALE_WATCHER_MAX_BACKOFF_MS = 10 * 60_000;

export interface LatLng {
  lat: number;
  lng: number;
}
export interface TimedFix extends LatLng {
  at: number;
}

export type GpsWriteReason = "first" | "distance_jump" | "distance" | "interval" | "seed" | "stationary_confirm";

export function distanceMeters(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}

export function isValidFix(f: Partial<LatLng> | null | undefined): f is LatLng {
  return (
    !!f &&
    typeof f.lat === "number" &&
    typeof f.lng === "number" &&
    Number.isFinite(f.lat) &&
    Number.isFinite(f.lng) &&
    Math.abs(f.lat) <= 90 &&
    Math.abs(f.lng) <= 180 &&
    !(f.lat === 0 && f.lng === 0)
  );
}

export class GpsWritePolicy {
  private lastWriteAt = 0;
  private lastWritten: LatLng | null = null;

  /** Returns why this fix should be written, or null to skip it. */
  evaluate(fix: LatLng, now: number): GpsWriteReason | null {
    if (!isValidFix(fix)) return null;
    if (!this.lastWritten) return "first";
    const moved = distanceMeters(this.lastWritten, fix);
    if (moved > GPS_FORCE_DISTANCE_M) return "distance_jump";
    if (now - this.lastWriteAt < GPS_MAX_INTERVAL_MS) return null;
    return moved >= GPS_MIN_DISTANCE_M ? "distance" : "interval";
  }

  record(fix: LatLng, now: number): void {
    this.lastWritten = { lat: fix.lat, lng: fix.lng };
    this.lastWriteAt = now;
  }

  lastWrittenFix(): LatLng | null {
    return this.lastWritten ? { ...this.lastWritten } : null;
  }

  msSinceLastWrite(now: number): number {
    return this.lastWritten ? now - this.lastWriteAt : Number.POSITIVE_INFINITY;
  }

  reset(): void {
    this.lastWriteAt = 0;
    this.lastWritten = null;
  }
}

export const RECOVERY_SILENCE_MS = 2 * 60_000;
export const RECOVERY_STATIONARY_M = 50;
export const RECOVERY_MOVED_M = 75;
/** Recovery request options: LOW accuracy, cached position up to 2 min old. */
export const RECOVERY_POSITION_OPTIONS = {
  enableHighAccuracy: false,
  timeout: 10_000,
  maximumAge: 2 * 60_000,
} as const;

export type RecoveryOutcome =
  | "inactive"
  | "not_due"
  | "disarmed"
  | "busy"
  | "failed"
  | "stationary"
  | "stalled_restart"
  | "inconclusive";

export interface LowPowerRecoveryDeps {
  now: () => number;
  policy: GpsWritePolicy;
  isActive: () => boolean;
  /** ONE low-accuracy position request. Resolve null when unavailable. */
  requestPosition: () => Promise<LatLng | null>;
  /** Stationary confirmed (within ~50m of the last save). */
  onStationary: (fix: LatLng) => void;
  /** Watcher looks stalled (moved >75m) — restart it once. */
  restartWatcher: () => void | Promise<void>;
  diag?: (event: RecoveryDiagEvent, fields: Record<string, unknown>) => void;
}

export type RecoveryDiagEvent =
  | "recovery_requested"
  | "recovery_succeeded"
  | "recovery_failed"
  | "recovery_stationary"
  | "recovery_watcher_restarted";

/**
 * Low-power recovery. NOT a heartbeat and has NO timer of its own: the owner
 * calls check() from the existing watcher-health interval. At most ONE
 * position request per silent period — after an attempt it stays disarmed
 * until the native watcher delivers a real reading (onWatcherReading()).
 * Failure therefore backs off naturally; nothing polls the GPS.
 */
export class LowPowerRecovery {
  private armed = true;
  private inFlight = false;
  constructor(private deps: LowPowerRecoveryDeps) {}

  /** A real native watcher reading arrived — re-arm for the next silent period. */
  onWatcherReading(): void {
    this.armed = true;
  }

  get isArmed(): boolean {
    return this.armed;
  }

  async check(): Promise<RecoveryOutcome> {
    const d = this.deps;
    if (!d.isActive()) return "inactive";
    const since = d.policy.msSinceLastWrite(d.now());
    if (since < RECOVERY_SILENCE_MS) return "not_due";
    if (this.inFlight) return "busy";
    if (!this.armed) return "disarmed";
    this.armed = false;
    this.inFlight = true;
    const msSinceWrite = Number.isFinite(since) ? Math.round(since) : null;
    d.diag?.("recovery_requested", { ms_since_last_write: msSinceWrite });
    let fix: LatLng | null = null;
    try {
      fix = await d.requestPosition();
    } catch {
      fix = null;
    } finally {
      this.inFlight = false;
    }
    if (!fix || !isValidFix(fix)) {
      d.diag?.("recovery_failed", { outcome: "no_position" });
      return "failed";
    }
    if (!d.isActive()) return "inactive";
    d.diag?.("recovery_succeeded", {});
    const last = d.policy.lastWrittenFix();
    if (!last) {
      d.diag?.("recovery_failed", { outcome: "no_reference" });
      return "inconclusive";
    }
    const moved = distanceMeters(last, fix);
    if (moved <= RECOVERY_STATIONARY_M) {
      d.diag?.("recovery_stationary", { moved_m: Math.round(moved) });
      d.onStationary(fix);
      return "stationary";
    }
    if (moved > RECOVERY_MOVED_M) {
      d.diag?.("recovery_watcher_restarted", { moved_m: Math.round(moved) });
      await d.restartWatcher();
      return "stalled_restart";
    }
    return "inconclusive";
  }

  reset(): void {
    this.armed = true;
    this.inFlight = false;
  }
}

export class StaleWatcherGuard {
  private lastRestartAt = 0;
  private consecutive = 0;

  shouldRestart(now: number, lastCallbackAt: number): boolean {
    if (lastCallbackAt > this.lastRestartAt) this.consecutive = 0;
    if (now - lastCallbackAt < STALE_WATCHER_MS) return false;
    if (this.lastRestartAt > 0) {
      const gap = Math.min(STALE_WATCHER_MS * 2 ** this.consecutive, STALE_WATCHER_MAX_BACKOFF_MS);
      if (now - this.lastRestartAt < gap) return false;
    }
    return true;
  }

  markRestart(now: number): void {
    this.lastRestartAt = now;
    this.consecutive += 1;
  }

  get consecutiveRestarts(): number {
    return this.consecutive;
  }

  reset(): void {
    this.lastRestartAt = 0;
    this.consecutive = 0;
  }
}

export interface QueuedFix extends LatLng {
  recordedAt: number;
}

export interface GpsRetryQueueOptions {
  maxSize?: number;
  maxAgeMs?: number;
  backoffMs?: number[];
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
}

export class GpsRetryQueue {
  private items: QueuedFix[] = [];
  private attempt = 0;
  private timer: unknown = null;
  private flushing = false;
  private readonly maxSize: number;
  private readonly maxAgeMs: number;
  private readonly backoffMs: number[];
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (t: unknown) => void;

  constructor(private send: (fix: QueuedFix) => Promise<void>, opts: GpsRetryQueueOptions = {}) {
    this.maxSize = opts.maxSize ?? 20;
    this.maxAgeMs = opts.maxAgeMs ?? 10 * 60_000;
    this.backoffMs = opts.backoffMs ?? [5_000, 15_000, 30_000, 60_000];
    this.now = opts.now ?? (() => Date.now());
    this.setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = opts.clearTimer ?? ((t) => clearTimeout(t as ReturnType<typeof setTimeout>));
  }

  get size(): number {
    return this.items.length;
  }

  snapshot(): QueuedFix[] {
    return [...this.items];
  }

  enqueue(fix: QueuedFix): void {
    this.items.push(fix);
    this.items.sort((a, b) => a.recordedAt - b.recordedAt);
    while (this.items.length > this.maxSize) this.items.shift();
    this.prune();
    this.schedule();
  }

  private prune(): void {
    const cutoff = this.now() - this.maxAgeMs;
    this.items = this.items.filter((f) => f.recordedAt >= cutoff);
  }

  private schedule(): void {
    if (this.timer || this.flushing || this.items.length === 0) return;
    const delay = this.backoffMs[Math.min(this.attempt, this.backoffMs.length - 1)];
    this.timer = this.setTimer(() => {
      this.timer = null;
      void this.flush();
    }, delay);
  }

  /** Send queued fixes oldest-first; stops at the first failure and backs off. */
  async flush(): Promise<void> {
    if (this.flushing) return;
    if (this.timer) {
      this.clearTimer(this.timer);
      this.timer = null;
    }
    this.flushing = true;
    try {
      this.prune();
      while (this.items.length > 0) {
        const next = this.items[0];
        try {
          await this.send(next);
          this.items.shift();
          this.attempt = 0;
        } catch {
          this.attempt += 1;
          break;
        }
        this.prune();
      }
    } finally {
      this.flushing = false;
      this.schedule();
    }
  }

  clear(): void {
    if (this.timer) this.clearTimer(this.timer);
    this.timer = null;
    this.items = [];
    this.attempt = 0;
  }
}
